import { useEffect, useState } from 'react'
import { RuuviTag, RuuviTagBattery, RuuviTagCondition } from '../types'
import {
  getRuuviTag,
  getRuuviTagBatteryNow,
  getRuuviTagConditionNow
} from '../services/ruuviTagService'
import { AxiosRequestConfig } from 'axios'

interface RuuviTagLoader {
  ruuviTag: RuuviTag | null
  loading: boolean
  error: unknown | null
}

export function useRuuviTagLoader(id: string): RuuviTagLoader {
  /**
   * The RuuviTag state.
   */
  const [ruuviTag, setRuuviTag] = useState<RuuviTag | null>(null)

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(true)

  /**
   * The error state.
   */
  const [error, setError] = useState<unknown | null>(null)

  /**
   * Load the RuuviTag by ID.
   */
  async function loadRuuviTag(id: string, config: AxiosRequestConfig = {}): Promise<void> {
    try {
      setLoading(true)
      setError(null)
      setRuuviTag(await getRuuviTag(id, config))
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Load the RuuviTag when the id changes.
   */
  useEffect(() => {
    const controller = new AbortController()
    loadRuuviTag(id, { signal: controller.signal })
    return () => controller.abort()
  }, [id])

  return {
    ruuviTag,
    loading,
    error
  }
}

export function useRuuviTagConditionLoader(id: string) {
  const [ruuviTagCondition, setRuuviTagCondition] = useState<RuuviTagCondition | null>(null)

  const [loading, setLoading] = useState<boolean>(true)

  const [error, setError] = useState<unknown | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    loadRuuviTagCondition(id, controller)
    return () => controller.abort()
  }, [id])

  useEffect(() => {
    const controller = new AbortController()

    const ONE_MINUTE = 60 * 1000
    const interval = setInterval(() => {
      loadRuuviTagCondition(id, controller)
    }, ONE_MINUTE)

    return () => {
      controller.abort()
      clearInterval(interval)
    }
  }, [id])

  async function loadRuuviTagCondition(id: string, controller: AbortController): Promise<void> {
    try {
      setLoading(true)
      setError(null)

      setRuuviTagCondition(await getRuuviTagConditionNow(id, { signal: controller.signal }))
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  return {
    ruuviTagCondition,
    loading,
    error
  }
}

export function useRuuviTagBatteryLoader(id: string) {
  const [ruuviTagBattery, setRuuviTagBattery] = useState<RuuviTagBattery | null>(null)

  const [loading, setLoading] = useState<boolean>(true)

  const [error, setError] = useState<unknown | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    loadRuuviTagBattery(id, controller)
    return () => controller.abort()
  }, [id])

  useEffect(() => {
    const controller = new AbortController()

    const ONE_MINUTE = 60 * 1000
    const interval = setInterval(() => {
      loadRuuviTagBattery(id, controller)
    }, ONE_MINUTE)

    return () => {
      controller.abort()
      clearInterval(interval)
    }
  }, [id])

  async function loadRuuviTagBattery(id: string, controller: AbortController): Promise<void> {
    try {
      setLoading(true)
      setError(null)

      setRuuviTagBattery(await getRuuviTagBatteryNow(id, { signal: controller.signal }))
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  return {
    ruuviTagBattery,
    loading,
    error
  }
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-unused-vars
interface RuuviTagAlertTemmplateLoader {}

export function useRuuviTagAlertTemplateLoader() {}
